import { ENV } from "../constants/environment";
import { PUBLIC_FILE_URL } from "../constants/urls";

export const STORED_FILE_ID_GUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isStoredFileId = (value) =>
  typeof value === "string" && STORED_FILE_ID_GUID.test(value.trim());

const stripGuidExtension = (value) => {
  const trimmed = String(value || "").trim();
  if (isStoredFileId(trimmed)) return trimmed;
  const withoutExt = trimmed.replace(/\.[^.]+$/, "");
  return isStoredFileId(withoutExt) ? withoutExt : null;
};

/** DTO / string içinden storedFileId (GUID) çıkarır. Eski dosya adları için null döner. */
export const resolveStoredFileId = (item) => {
  if (!item) return null;
  if (typeof item === "string") return stripGuidExtension(item);
  const storedFileId =
    stripGuidExtension(item.storedFileId) ||
    stripGuidExtension(item.StoredFileId) ||
    stripGuidExtension(item.profilePhotoName) ||
    stripGuidExtension(item.certificateFileName);
  if (storedFileId) return storedFileId;
  // Randevu belge kaydı id'si storedFileId değildir; yalnızca GUID ise documentName dene.
  if (item.bookedAppointmentId || item.BookedAppointmentId) {
    return stripGuidExtension(item.documentName);
  }
  return stripGuidExtension(item.documentName) || stripGuidExtension(item.id);
};

export const getPublicFileUrl = (storedFileId, variant = "Thumbnail") => {
  const id = resolveStoredFileId(storedFileId);
  if (!id) return null;
  return `${ENV.apiUrl}${PUBLIC_FILE_URL}/${id}/download?variant=${variant}`;
};

export const getFileExtension = (name = "") => {
  const value = String(name || "");
  if (!value.includes(".")) return "";
  return value.split(".").pop().toLowerCase();
};

export const mapAttachmentType = (contentType = "", fileName = "") => {
  const mime = String(contentType || "").toLowerCase();
  const ext = getFileExtension(fileName);
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return 1;
  if (mime.startsWith("audio/") || ["mp3", "m4a", "aac", "wav"].includes(ext)) return 3;
  if (mime.startsWith("video/") || ["mp4", "avi", "mov"].includes(ext)) return 4;
  return 2;
};

/** Hub / API (PascalCase veya camelCase) eklerini UI formatına çevirir */
export const normalizeMessageAttachment = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const fileName = raw.fileName || raw.FileName || raw.originalFileName || raw.OriginalFileName || "";
  const mimeType = raw.mimeType || raw.MimeType || raw.contentType || raw.ContentType || "";
  const storedFileId =
    resolveStoredFileId(raw.storedFileId) ||
    resolveStoredFileId(raw.StoredFileId) ||
    resolveStoredFileId(raw.id) ||
    resolveStoredFileId(raw.Id);
  if (!storedFileId && !fileName) return null;
  const fileSize = raw.fileSize ?? raw.FileSize ?? raw.sizeBytes ?? raw.SizeBytes ?? 0;
  const type =
    raw.type ?? raw.Type ?? mapAttachmentType(mimeType, fileName);
  return {
    id: raw.id || raw.Id || storedFileId,
    storedFileId,
    fileName,
    mimeType,
    fileSize,
    type,
  };
};

export const normalizeMessageAttachments = (rawAttachments) => {
  const list = Array.isArray(rawAttachments)
    ? rawAttachments
    : Array.isArray(rawAttachments?.$values)
      ? rawAttachments.$values
      : [];
  return list.map(normalizeMessageAttachment).filter(Boolean);
};

/** SignalR / API mesaj gövdesini chat UI’sinin beklediği şekle getirir */
export const normalizeChatMessage = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  const attachments = normalizeMessageAttachments(
    raw.attachments ?? raw.Attachments
  );
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    roomId: raw.roomId ?? raw.RoomId,
    senderId: raw.senderId ?? raw.SenderId ?? raw.userId ?? raw.UserId,
    content: raw.content ?? raw.Content ?? "",
    createdAt: raw.createdAt ?? raw.CreatedAt,
    attachments,
  };
};

export const loadPrivateFileBlobs = async (getPrivateFile, items) => {
  const files = [];
  for (const item of items || []) {
    const fileId = resolveStoredFileId(item);
    if (!fileId) continue;
    const name =
      typeof item === "string"
        ? item
        : item.originalDocumentName ||
          item.originalFileName ||
          item.fileName ||
          item.documentName ||
          item.name ||
          fileId;
    try {
      const file = await getPrivateFile(fileId).unwrap();
      if (typeof file !== "string") continue;
      files.push({
        file,
        name,
        ext: getFileExtension(name),
        id: item?.id,
        storedFileId: fileId,
        isDbItem: true,
      });
    } catch (_) {
      /* skip inaccessible files */
    }
  }
  return files;
};
